/**
 * Marketplace order lists (Axiom, Partell) — the filters the order pages and
 * a Marketplace Orders contact list share (SCRUM-2207).
 *
 * Canonical keys only (lib/filterRegistry FILTER_KEYS). The pages send them as
 * `filtersArray` to GET /integration-orders, and a contact list stores them as
 * its segment: both resolve through the API's `integration_order` pipeline
 * (FilterNormalizer → ListFilterService), so they select the same orders.
 */
import { buildMarketingFilterConditions, FILTER_KEYS } from '@/lib/filterRegistry';
import { computeSupersededBy, isDueForReorder } from '@/lib/peptideOrder';
import { isReadyForPickup } from '@/lib/orderHandoff';
import type { IntegrationOrder } from '@/services/integrations.service';
import type { FilterConfig, FilterNegations, FilterValue } from '@/types/filters.types';

export const INTEGRATION_ORDER_SOURCE_TYPE = 'integration_order';

export const INTEGRATION_ORDER_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search patient, order #, product…' },
  { key: FILTER_KEYS.ORDER_STATUS, label: 'Status', type: 'select', allLabel: 'Any status', multi: true },
  { key: FILTER_KEYS.FACILITY, label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
  { key: FILTER_KEYS.ORDER_PRESCRIBER, label: 'Prescriber', type: 'select', allLabel: 'Any prescriber', multi: true },
  { key: FILTER_KEYS.ORDERED_BY, label: 'Ordered by', type: 'select', allLabel: 'Anyone', multi: true },
  { key: FILTER_KEYS.INCLUDE_CANCELLED, label: 'Cancelled orders', type: 'select', allLabel: 'Hidden', multi: true },
  // No defaultDatePreset: a list saved from the page must not inherit a window.
  { key: FILTER_KEYS.DATERANGE, label: 'Order Date', type: 'daterange' },
];

/** The Contact List page edits the same filters, plus which vendor (the pages are one vendor each). */
export const INTEGRATION_ORDER_LIST_FILTER_CONFIGS: FilterConfig[] = [
  { key: FILTER_KEYS.INTEGRATION, label: 'Vendor', type: 'select', allLabel: 'Any vendor', multi: true },
  ...INTEGRATION_ORDER_FILTER_CONFIGS.filter((c) => c.type !== 'search'),
];

/** Option value for an order with no facility (FilterNormalizer reads 0 as "none"). */
export const NO_FACILITY = '0';

/** A fulfillment status an order is still being worked in (the In Fulfillment tile). */
export const AXIOM_IN_FLIGHT_STATUSES = [
  'scheduled', 'submitting', 'received', 'routed', 'in_production', 'ready_to_pack', 'ready_to_ship', 'shipped',
];
export const RMM_IN_FLIGHT_STATUSES = ['scheduled', 'submitting', 'received', 'processed', 'shipped'];
/** The Awaiting Payment tile. */
export const AWAITING_PAYMENT_STATUSES = ['awaiting_payment'];
/** Every status a page's KPI tile can select, so the Status filter always offers it. */
export const AXIOM_TILE_STATUSES = [...AWAITING_PAYMENT_STATUSES, ...AXIOM_IN_FLIGHT_STATUSES];
export const RMM_TILE_STATUSES = [...AWAITING_PAYMENT_STATUSES, ...RMM_IN_FLIGHT_STATUSES];

export interface AxiomOrderKpis {
  total: number;
  awaitingPayment: number;
  inFlight: number;
  reorderDue: number;
  readyForPickup: number;
}

/**
 * The Peptides page's KPI tiles for one product line (pens or vials), shared
 * with the Marketplace dashboard so both show the same numbers. `inScope` is
 * the facility scope; a re-order an order has already been replaced by does
 * not count as due, judged across the whole line.
 */
export function axiomOrderKpis(
  orders: IntegrationOrder[],
  inScope: (o: IntegrationOrder) => boolean = () => true,
): AxiomOrderKpis {
  const supersededBy = computeSupersededBy(orders, (o) => o.patientId ?? 0);
  const kpis: AxiomOrderKpis = { total: 0, awaitingPayment: 0, inFlight: 0, reorderDue: 0, readyForPickup: 0 };
  for (const o of orders) {
    if (!inScope(o)) continue;
    const fs = o.fulfillmentStatus ?? '';
    kpis.total += 1;
    if (fs === 'awaiting_payment') kpis.awaitingPayment += 1;
    if (AXIOM_IN_FLIGHT_STATUSES.includes(fs)) kpis.inFlight += 1;
    if (isDueForReorder(o, supersededBy)) kpis.reorderDue += 1;
    if (isReadyForPickup(o)) kpis.readyForPickup += 1;
  }
  return kpis;
}

/**
 * The page's filters as a Marketplace Orders audience for MarketingActionsModal:
 * buildMarketingFilterConditions, scoped to the page's vendor, stored as pane
 * filters so the legacy stack never reads it as "every patient".
 */
export function integrationOrderMarketingConditions(
  integrationKey: string,
  values: Record<string, FilterValue>,
  negations: FilterNegations,
): Record<string, unknown> {
  const { search: _search, ...listValues } = values;
  void _search;
  return {
    mode: 'pane_filters',
    ...buildMarketingFilterConditions(
      INTEGRATION_ORDER_SOURCE_TYPE,
      { ...listValues, [FILTER_KEYS.INTEGRATION]: [integrationKey] },
      negations,
    ),
  };
}

/**
 * Why the page's filters can't be saved as an Email / SMS list, or null when
 * they can. Search narrows the table on the server but is not a list filter
 * (the Contact List page has no Search to show or keep it), so a list saved
 * while one is typed would hold every order the other filters match.
 * The same goes for the Due for Re-order tile: it is worked out from each
 * order's supply on the page, not a filter a list can keep.
 */
export function marketingListBlockedReason(
  values: Record<string, FilterValue>,
  opts: { reorderDue?: boolean; readyForPickup?: boolean } = {},
): string | null {
  const search = values.search;
  if (Array.isArray(search) && search.some((t) => String(t).trim() !== '')) {
    return 'Clear the search to build a list. Lists keep the filters, not the search, so it would include every order the other filters match.';
  }
  if (opts.reorderDue) {
    return 'Turn off Due for Re-order to build a list. Lists keep the filters, not this tile, so it would include every order the other filters match.';
  }
  if (opts.readyForPickup) {
    return 'Turn off Ready for pickup to build a list. Lists keep the filters, not this tile, so it would include every order the other filters match.';
  }
  return null;
}

/** Whether the select filter holds exactly these values (a KPI tile's selected state). */
export function selectEquals(value: FilterValue | undefined, wanted: string[]): boolean {
  if (!Array.isArray(value) || value.length !== wanted.length) return false;
  const set = new Set(value);
  return wanted.every((w) => set.has(w));
}
